import React from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import { 
  Flame, CheckCircle, XCircle, RefreshCw, UserX,
  MessageCircle, Heart, DollarSign, Clock,
  UserCheck, BotOff, Calendar, CalendarCheck,
  Phone, MessageSquare, Rocket, Pause, Play, 
  Upload, Activity, Loader2, ArrowRightLeft
} from 'lucide-react';
import { useActivityFeed, formatRelativeTime, getActivityColor } from '../hooks/useActivityFeed';
import { useSystemHealth } from '../hooks/useSystemHealth';
import styles from '../AIControlRoomAdaptive.module.css';

// Icon mapping for activity types
const iconMap = {
  lead_hot: Flame,
  lead_engaging: MessageCircle,
  lead_qualified: CheckCircle, // retired — kept so historical rows still render
  lead_disqualified: XCircle,
  lead_reengaged: RefreshCw,
  lead_opted_out: UserX,
  first_reply: MessageCircle,
  high_sentiment: Heart,
  pricing_inquiry: DollarSign,
  timeline_mentioned: Clock,
  handoff_triggered: UserCheck,
  ai_disabled: BotOff,
  booking_in_progress: Calendar,
  booking_confirmed: CalendarCheck,
  call_logged: Phone,
  manual_sms: MessageSquare,
  ai_sms_sent: MessageSquare,
  campaign_launched: Rocket,
  campaign_paused: Pause,
  campaign_resumed: Play,
  bulk_upload_completed: Upload,
  leads_transferred: ArrowRightLeft,
  default: Activity
};

const SystemHealth = ({ selectedTenant, channel = 'sms' }) => {
  const { activities, loading, error } = useActivityFeed(5, selectedTenant);
  const { healthMetrics, loading: metricsLoading, error: metricsError } = useSystemHealth(selectedTenant, channel);

  const getIcon = (eventType) => {
    return iconMap[eventType] || iconMap.default;
  };

  return (
    <div className={styles.rightPanel}>
      <div className={styles.healthSection}>
        <h3 className={styles.healthTitle}>System Health</h3>
        
        {healthMetrics.map((metric, index) => (
          <div key={index}>
            <div className={styles.healthMetric}>
              <div className={styles.healthLabel}>{metric.label}</div>
              <div 
                className={styles.healthValue}
                style={{ '--health-color': metric.color }}
              >
                {metric.value}
              </div>
            </div>
            
            <div className={styles.healthBar}>
              <motion.div
                className={styles.healthBarFill}
                initial={{ width: 0 }}
                animate={{ width: `${metric.percentage}%` }}
                transition={{ duration: 1, ease: 'easeOut' }}
                style={{ '--health-color': metric.color }}
              />
            </div>
          </div>
        ))}
      </div>
      
      <div className={styles.activityLog}>
        <h3 className={styles.healthTitle}>Recent Activity</h3>
        
        {loading ? (
          <div className={styles.loadingState}>
            <Loader2 className={styles.spinner} size={20} />
            <span>Loading activity...</span>
          </div>
        ) : error ? (
          <div className={styles.errorState}>
            Unable to load activity
          </div>
        ) : activities.length === 0 ? (
          <div className={styles.emptyState}>
            No recent activity
          </div>
        ) : (
          <AnimatePresence mode="popLayout">
            {activities.map((activity) => {
              const IconComponent = getIcon(activity.event_type);
              const color = getActivityColor(activity.event_type);
              
              return (
                <motion.div
                  key={activity.id}
                  className={styles.logItem}
                  initial={{ opacity: 0, x: -20, height: 0 }}
                  animate={{ opacity: 1, x: 0, height: 'auto' }}
                  exit={{ opacity: 0, x: 20, height: 0 }}
                  transition={{ duration: 0.3 }}
                  layout
                >
                  <div className={styles.logTime}>
                    {formatRelativeTime(activity.created_at)}
                  </div>
                  <div className={styles.logContent}>
                    <IconComponent 
                      size={14} 
                      style={{ color, flexShrink: 0 }} 
                    />
                    <span className={styles.logText}>
  {activity.lead_id && activity.lead_name ? (
    <>
      <a
        href={`/lead/${activity.lead_id}`}
        className={styles.logLeadLink}
        onClick={(e) => {
          e.preventDefault();
          window.location.href = `/lead/${activity.lead_id}`;
        }}
      >
        {activity.lead_name}
      </a>
      {' '}
      {(() => {
        const name = activity.lead_name;
        const title = activity.title || '';
        // Strip lead name from the start of the title to avoid duplication
        const stripped = title.startsWith(name)
          ? title.slice(name.length).trimStart()
          : title.replace(name, '').trimStart();
        return stripped;
      })()}
    </>
  ) : (
    activity.title
  )}
</span>
                  </div>
                </motion.div>
              );
            })}
          </AnimatePresence>
        )}
      </div>
    </div>
  );
};

export default SystemHealth;